<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Chương trình thưởng AdHoc V2'">
          <template v-slot:toolbar>
            <div class="row">
              <div class="ml-3">
                <button
                  type="button"
                  class="btn btn-primary font-weight-bolder btn-sm"
                  @click="addNew"
                >
                  <i style="font-size: 1rem" class="flaticon2-add-1"></i>Tạo mới
                </button>
              </div>
            </div>
          </template>
          <template v-slot:preview>
            <!-- Bộ lọc -->
            <div class="col-md-12 mb-5 pl-0">
              <b-row>
                <b-col md="3" class="pl-0">
                  <b-input
                    size="sm"
                    placeholder="Tìm theo tên chương trình"
                    v-model="filter.keyword"
                    autocomplete="off"
                    v-on:input="debounceSearch"
                  ></b-input>
                </b-col>
                <b-col md="2">
                  <b-form-select
                    size="sm"
                    v-model="filter.status"
                    :options="statusOptions"
                    value-field="id"
                    text-field="name"
                    @change="onFilter"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null"
                        >-- Tất cả trạng thái --</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-col>
                <b-col md="2">
                  <date-picker
                    placeholder="Từ ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.fromDate"
                  ></date-picker>
                </b-col>
                <b-col md="2">
                  <date-picker
                    placeholder="Đến ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.toDate"
                  ></date-picker>
                </b-col>
                <b-col md="1">
                  <b-button
                    style="width: 70px"
                    variant="primary"
                    size="sm"
                    @click="onFilter"
                    >Lọc</b-button
                  >
                </b-col>
              </b-row>
            </div>

            <b-table
              :items="items"
              :fields="fields"
              bordered
              hover
              :busy="onLoading"
              show-empty
              empty-text="Không có chương trình AdHoc nào"
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="7"></vcl-table>
              </template>
              <template v-slot:cell(count)="row">
                {{ (page - 1) * PAGE_LIMIT + row.index + 1 }}
              </template>
              <template v-slot:cell(period)="row">
                <span>{{ formatShortDate(row.item.startDate) }}</span>
                <span> — </span>
                <span v-if="row.item.endDate">{{
                  formatShortDate(row.item.endDate)
                }}</span>
                <span v-else class="text-muted">không giới hạn</span>
              </template>
              <!-- Phạm vi áp dụng: bán lẻ + bán buôn tách riêng cho dễ soát -->
              <template v-slot:cell(scope)="row">
                <div>
                  <b-badge
                    v-if="row.item.applyAllRetailStores"
                    variant="success"
                    class="mr-1"
                    >Toàn bộ bán lẻ</b-badge
                  >
                  <b-badge
                    v-else-if="row.item.retailStoreCount"
                    variant="light"
                    class="mr-1"
                    >{{ row.item.retailStoreCount }} CH bán lẻ</b-badge
                  >
                </div>
                <div class="mt-1">
                  <b-badge
                    v-if="row.item.applyAllWholesaleStores"
                    variant="info"
                    class="mr-1"
                    >Toàn bộ bán buôn</b-badge
                  >
                  <b-badge
                    v-else-if="row.item.wholesaleStoreCount"
                    variant="light"
                    class="mr-1"
                    >{{ row.item.wholesaleStoreCount }} CH bán buôn</b-badge
                  >
                </div>
              </template>
              <template v-slot:cell(status)="row">
                <span
                  class="label font-weight-bold label-lg label-inline"
                  :class="statusVariant(row.item.status)"
                  >{{ statusName(row.item.status) }}</span
                >
              </template>
              <template v-slot:cell(actions)="row">
                <div class="d-flex justify-content-center">
                  <b-dropdown size="sm" no-caret right>
                    <template slot="button-content">
                      <i
                        style="font-size: 1rem; padding-right: 0px"
                        class="flaticon2-settings"
                      ></i>
                    </template>
                    <b-dropdown-item @click="editItem(row.item)">
                      <span style="color: #3f4254; font-size: 12px">
                        <i style="font-size: 1rem" class="flaticon2-pen"></i>
                        &nbsp; Chỉnh sửa
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="showStatusModal(row.item)">
                      <span style="color: #3f4254; font-size: 12px">
                        <i
                          style="font-size: 1rem"
                          class="fas fa-exchange-alt"
                        ></i>
                        &nbsp; Đổi trạng thái
                      </span>
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>
            </b-table>

            <b-row>
              <b-col>
                <p class="mt-3 text-dark" style="font-weight: 500">
                  Tổng số chương trình: {{ totalItems }}
                </p>
              </b-col>
              <b-col>
                <b-pagination-nav
                  class="custom-pagination"
                  v-show="totalPages >= 2"
                  :link-gen="linkGen"
                  :number-of-pages="totalPages"
                  use-router
                  @change="fetchData"
                  align="right"
                  first-class="page-item-first btn btn-icon btn-sm mr-1 my-1"
                  prev-class="page-item-prev btn btn-icon btn-sm mr-.5 my-1"
                  next-class="page-item-next btn btn-icon btn-sm mr-1 my-1 ml-.5"
                  last-class="page-item-last btn btn-icon btn-sm my-1"
                  page-class="btn btn-icon btn-sm border-0 mr-.5 my-1"
                >
                  <template v-slot:first-text>
                    <span>
                      <i class="ki ki-bold-double-arrow-back icon-xs"></i>
                    </span>
                  </template>
                  <template v-slot:prev-text>
                    <i class="ki ki-bold-arrow-back icon-xs"></i>
                  </template>
                  <template v-slot:next-text>
                    <i class="ki ki-bold-arrow-next icon-xs"></i>
                  </template>
                  <template v-slot:last-text>
                    <span class="text-info">
                      <i class="ki ki-bold-double-arrow-next icon-xs"></i>
                    </span>
                  </template>
                </b-pagination-nav>
              </b-col>
            </b-row>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- Đổi trạng thái -->
    <b-modal
      v-model="statusModalVisible"
      title="Đổi trạng thái chương trình"
      @ok="confirmChangeStatus"
      ok-title="Cập nhật"
      cancel-title="Huỷ"
      :ok-disabled="isUpdatingStatus"
    >
      <p class="mb-3">
        Chương trình: <strong>{{ statusModel.name }}</strong>
      </p>
      <b-form-select
        size="sm"
        v-model="statusModel.status"
        :options="statusOptions"
        value-field="id"
        text-field="name"
      ></b-form-select>
    </b-modal>
  </div>
</template>

<script>
/**
 * Danh sách chương trình thưởng AdHoc V2 — SRS F-06.
 *
 * KHÔNG có xoá: chương trình đã phát sinh tiền thưởng nên phải giữ lại để truy vết.
 * Muốn dừng thì đổi trạng thái sang "Ngưng áp dụng".
 * Màn MỚI, không dùng lại /reward/reward-adhoc cũ.
 *
 * Điểm khác bản cũ: cột "Phạm vi" hiện rõ áp dụng cho bao nhiêu cửa hàng
 * BÁN LẺ và bao nhiêu cửa hàng BÁN BUÔN (hoặc cờ áp dụng toàn bộ kênh).
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import debounce from 'debounce';
import moment from 'moment';
import datePicker from 'vue-bootstrap-datetimepicker';
import { VclTable } from 'vue-content-loading';
import { TIME_TRIGGER } from '@/utils/constants';
import { formatDate, makeToastSuccess, makeToastFaile } from '@/utils/common';
import {
  COMMISSION_ADHOC_STATUS,
  COMMISSION_ADHOC_STATUS_NAME,
  COMMISSION_ADHOC_STATUS_VARIANT,
  toOptionList,
} from './commission.constants';

const PAGE_LIMIT = 10;
const DATE_INPUT_FORMAT = 'DD/MM/YYYY';
const DATE_API_FORMAT = 'YYYY-MM-DD';

export default {
  components: { KTCodePreview, VclTable, datePicker },
  data() {
    return {
      PAGE_LIMIT,
      onLoading: false,
      isUpdatingStatus: false,
      items: [],
      totalPages: 0,
      totalItems: 0,
      page: 1,
      filter: { keyword: '', status: null, fromDate: '', toDate: '' },
      statusModalVisible: false,
      statusModel: { id: null, name: '', status: COMMISSION_ADHOC_STATUS.ACTIVE },
      statusOptions: toOptionList(COMMISSION_ADHOC_STATUS_NAME),
      dpConfigs: {
        date: {
          format: DATE_INPUT_FORMAT,
          useCurrent: false,
          showClear: true,
          showClose: true,
          locale: 'vi',
        },
      },
      linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
      fields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
          tdClass: 'text-center',
        },
        {
          key: 'name',
          label: 'Tên chương trình',
          thStyle: { fontWeight: 600, color: '#181c32', width: '28%' },
        },
        {
          key: 'period',
          label: 'Thời gian áp dụng',
          thStyle: { fontWeight: 600, color: '#181c32', width: '20%' },
        },
        {
          key: 'scope',
          label: 'Phạm vi áp dụng',
          thStyle: { fontWeight: 600, color: '#181c32', width: '18%' },
        },
        {
          key: 'detailCount',
          label: 'Số dòng',
          thStyle: { fontWeight: 600, color: '#181c32', width: '9%' },
          tdClass: 'text-center',
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { fontWeight: 600, color: '#181c32', width: '13%' },
          tdClass: 'text-center',
        },
        {
          key: 'actions',
          label: '',
          thStyle: { fontWeight: 600, color: '#181c32', width: '7%' },
        },
      ],
    };
  },
  created() {
    this.page = Number(this.$route.query.page) || 1;
    this.fetchData();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng', route: '/commission/categories' },
      { title: 'Chương trình thưởng AdHoc' },
    ]);
  },
  methods: {
    statusName(status) {
      return COMMISSION_ADHOC_STATUS_NAME[Number(status)] || '';
    },
    statusVariant(status) {
      return COMMISSION_ADHOC_STATUS_VARIANT[Number(status)] || 'label-light';
    },
    formatShortDate(value) {
      return value ? formatDate(value, DATE_INPUT_FORMAT) : '';
    },
    /** 'DD/MM/YYYY' -> 'YYYY-MM-DD'; bỏ qua nếu người dùng gõ dở */
    toApiDate(value) {
      if (!value) {
        return undefined;
      }
      const parsed = moment(value, DATE_INPUT_FORMAT, true);
      return parsed.isValid() ? parsed.format(DATE_API_FORMAT) : undefined;
    },
    debounceSearch: debounce(function () {
      this.onFilter();
    }, TIME_TRIGGER),
    onFilter() {
      this.page = 1;
      this.fetchData();
    },
    fetchData(pageNum) {
      if (pageNum) {
        this.page = pageNum;
      }
      this.onLoading = true;
      const params = {
        page: this.page,
        limit: PAGE_LIMIT,
        keyword: this.filter.keyword || undefined,
        status: this.filter.status || undefined,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };
      ApiService.query('commission-adhocs', { params })
        .then(({ data }) => {
          this.items = data.data.items || [];
          this.totalItems = data.data.pagination.totalItems || 0;
          this.totalPages = data.data.pagination.totalPages || 0;
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được danh sách'),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },
    addNew() {
      this.$router.push({ name: 'commission-upsert-adhoc' });
    },
    editItem(item) {
      this.$router.push({
        name: 'commission-upsert-adhoc',
        query: { id: item.id },
      });
    },
    showStatusModal(item) {
      this.statusModel = {
        id: item.id,
        name: item.name,
        status: Number(item.status),
      };
      this.statusModalVisible = true;
    },
    confirmChangeStatus() {
      this.isUpdatingStatus = true;
      ApiService.patch(`commission-adhocs/${this.statusModel.id}/status`, {
        status: this.statusModel.status,
      })
        .then(({ data }) => {
          makeToastSuccess(data.message);
          this.fetchData();
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không cập nhật được trạng thái'),
          );
        })
        .finally(() => {
          this.isUpdatingStatus = false;
        });
    },
  },
};
</script>
